/**
 * AUTH domain state.
 *
 * SIMULATED. There is no session, no token, and no persistence — a reload logs
 * you out. See src/api/authApi.ts for what the mock actually accepts.
 *
 * Scope note: this store holds *session and profile* state only. The login form's
 * own field values (email, password, confirm, error text) are local UI state and
 * live in the auth screen component, not here — they are transient input, not
 * application state, and putting them in a store would make every keystroke a
 * global update.
 */
import { create } from 'zustand';
import { authApi } from '../api/authApi';
import { toApiErrorShape, type ApiErrorShape } from '../api/errors';
import { setAuthToken, setUnauthorizedHandler } from '../api/http';
import { resetShowcaseSession } from '../hooks/useShowcase';
import type {
  AppAccess,
  AuthProviderId,
  CheckoutProductOwnership,
  Entitlement,
  LoginPayload,
  SignupPayload,
  User,
} from '../types/domain';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  /** Always null while auth is simulated. Present so callers can already read it. */
  token: string | null;
  /** Whether this signed-in account may enter the app beyond the upgrade screen. */
  appAccess: AppAccess;
  /** Which checkout-only marketing offers the account already owns. */
  checkoutOwnership: CheckoutProductOwnership;
  /** Server-confirmed feature entitlements used by upgrade-card ownership. */
  entitlements: Entitlement | undefined;
  /** True while a login/signup request is in flight. */
  pending: boolean;
  /**
   * Whether the "am I signed in?" question has been asked yet.
   *
   * ============================================================================
   * THE THREE-STATE PROBLEM THIS EXISTS TO SOLVE (Batch 7 §5, session restoration).
   * `isAuthenticated: false` currently means two different things that must be told
   * apart: "we asked the server and nobody is signed in" and "we have not asked yet".
   * With one boolean they are the same value, so a router gate reading it on the first
   * frame bounces a signed-in user to /login before the session check answers — the
   * classic flash-of-login-screen, and then a redirect back that loses the URL they
   * came for.
   *
   * FALSE UNTIL `restoreSession` COMPLETES, and it is deliberately NOT true at rest
   * today. Nothing calls `restoreSession` yet, because there is nothing to restore: no
   * token is issued and none is persisted, so a boot-time check could only ever answer
   * "nobody", and running it would add a real await to every start in exchange for a
   * guaranteed null.
   *
   * WHAT READS IT: `RequireAuth` in router.tsx, as of the Phase B session-gate wiring.
   * The gate shows a splash while this is false and nobody is known to be signed in —
   * which is the branch the note below always anticipated, added as a branch rather
   * than as a redesign of this store, exactly as intended.
   * ============================================================================
   */
  sessionChecked: boolean;

  /**
   * Why the session check failed, or null when it did not fail.
   *
   * ============================================================================
   * A FAILED RESTORATION IS NOT A SIGNED-OUT USER, and before this field the app could
   * not tell the two apart. `restoreSession` caught everything and set
   * `sessionChecked: true` with a null user, so a backend outage, a CORS
   * misconfiguration and a genuine "nobody is signed in" all produced the same state
   * and the same screen: the login form. That tells someone whose session is perfectly
   * valid that they are logged out, and invites them to type a password at a server
   * that cannot answer.
   *
   * NON-NULL ONLY WHEN THE CHECK ITSELF BROKE. `authApi.getSession()` resolves with
   * null for "nobody is signed in" — that is the happy path and it leaves this null.
   * So this being set means a real transport or server fault, which is why the gate
   * can safely render a retryable failure state from it rather than a login form.
   *
   * An `ApiErrorShape`, not an `ApiError`, for the reason api/errors.ts states at the
   * type: Zustand state is compared and snapshotted, and an Error's stack differs on
   * every throw. Same shape `LoadState.error` holds, so the gate's failure UI and every
   * screen's failure UI read the same fields.
   * ============================================================================
   */
  sessionError: ApiErrorShape | null;

  /**
   * Asks the server who is signed in, once, on boot.
   *
   * NOT CALLED ANYWHERE YET. See `sessionChecked` for why, and note what this is not:
   * it is not a mock of a restored session. `authApi.getSession()` answers truthfully
   * that there is no session, so this action's only observable effect today is setting
   * `sessionChecked` to true.
   *
   * NEVER REJECTS. A failed session check is not a failed sign-in — the app has to boot
   * either way — so the catch sets the checked flag and leaves the user null. That is
   * the one place in this store where swallowing an error is right, and it is the reason
   * `authApi.getSession` resolves rather than throwing: if the happy path already
   * returned null for "nobody", the catch is only reached by a genuine outage.
   *
   * AND THE OUTAGE IS NO LONGER SWALLOWED SILENTLY. The note above used to end "treating
   * an outage as signed-out is a conscious choice with one line to change" — this is that
   * line changed. The catch records `sessionError` so the gate can say "we could not
   * check your session" and offer a retry, instead of asserting that nobody is signed in.
   * The promise still never rejects; boot is still unconditional.
   */
  restoreSession: () => Promise<void>;

  login: (payload: LoginPayload) => Promise<void>;
  signup: (payload: SignupPayload) => Promise<void>;
  /** Starts Google sign-in through the backend. Apple and mock mode remain unsupported. */
  signInWithProvider: (provider: AuthProviderId) => Promise<void>;
  completeProviderSignIn: (
    provider: AuthProviderId,
    code: string,
    state: string
  ) => Promise<void>;
  verifyEmail: (token: string, newPassword: string) => Promise<void>;
  resendVerificationEmail: (email: string, turnstileToken: string) => Promise<void>;
  loginAsDemo: () => Promise<void>;
  logout: () => void;
  /** Clears the local session without calling the logout endpoint. */
  clearSession: () => void;
  /** Updates mutable profile fields. Account email is deliberately immutable. */
  updateProfile: (patch: Partial<Pick<User, 'name' | 'avatarUrl'>>) => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => {
  function clearSessionState() {
    // The public catalogue snapshot belongs to the signed-in app session that produced it.
    // Leaving it behind across logout would hand the next session a Home page it never read.
    resetShowcaseSession();
    // The token goes with it, before the state changes: a request in flight must not
    // be able to carry the credential of a session the user has just ended.
    setAuthToken(null);
    /*
      `sessionChecked` STAYS TRUE. Signing out is an answer to "who is signed in" —
      resetting it to false would make the gate show its checking splash over a
      deliberate sign-out, and the user would watch a spinner on their way to /login.
    */
    set({
      user: null,
      isAuthenticated: false,
      token: null,
      appAccess: 'purchase_required',
      checkoutOwnership: {},
      entitlements: undefined,
      pending: false,
      sessionChecked: true,
      sessionError: null,
    });
  }

  return {
    user: null,
    isAuthenticated: false,
    token: null,
    appAccess: 'purchase_required',
    checkoutOwnership: {},
    entitlements: undefined,
    pending: false,
    sessionChecked: false,
    sessionError: null,

    async restoreSession() {
      try {
        const session = await authApi.getSession();
        // Null today and for a documented reason (no token is ever issued), but this is
        // the one line that has to exist for a real one to reach the transport.
        setAuthToken(session.token);
        set({
          ...session,
          checkoutOwnership: session.checkoutOwnership ?? {},
          entitlements: session.entitlements,
          sessionChecked: true,
          sessionError: null,
        });
      } catch (err) {
        /*
          Still never rejects — the app must boot. What changed is that the reason is
          kept: see `sessionError`. `getSession` resolves for "nobody is signed in", so
          reaching this catch means the check itself failed, and the gate renders that as
          a retryable fault rather than as a logged-out user.
        */
        set({ sessionChecked: true, sessionError: toApiErrorShape(err) });
      }
    },

    async login(payload) {
      set({ pending: true });
      try {
        const session = await authApi.login(payload);
        setAuthToken(session.token);
        set({
          ...session,
          checkoutOwnership: session.checkoutOwnership ?? {},
          entitlements: session.entitlements,
          pending: false,
          sessionChecked: true,
          sessionError: null,
        });
      } catch (err) {
        set({ pending: false });
        throw err;
      }
    },

    async signup(payload) {
      set({ pending: true });
      try {
        const session = await authApi.signup(payload);
        setAuthToken(session.token);
        set({
          ...session,
          checkoutOwnership: session.checkoutOwnership ?? {},
          entitlements: session.entitlements,
          pending: false,
          sessionChecked: true,
          sessionError: null,
        });
      } catch (err) {
        set({ pending: false });
        throw err;
      }
    },

    async signInWithProvider(provider) {
      set({ pending: true });
      try {
        const session = await authApi.signInWithProvider(provider);
        setAuthToken(session.token);
        set({
          ...session,
          checkoutOwnership: session.checkoutOwnership ?? {},
          entitlements: session.entitlements,
          pending: false,
          sessionChecked: true,
          sessionError: null,
        });
      } catch (err) {
        set({ pending: false });
        throw err;
      }
    },

    async completeProviderSignIn(provider, code, state) {
      set({ pending: true });
      try {
        const session = await authApi.completeProviderSignIn({
          provider,
          code,
          state,
        });
        setAuthToken(session.token);
        set({
          ...session,
          checkoutOwnership: session.checkoutOwnership ?? {},
          entitlements: session.entitlements,
          pending: false,
          sessionChecked: true,
          sessionError: null,
        });
      } catch (err) {
        set({ pending: false });
        throw err;
      }
    },

    async verifyEmail(token, newPassword) {
      set({ pending: true });
      try {
        const session = await authApi.verifyEmail(token, newPassword);
        setAuthToken(session.token);
        set({
          ...session,
          checkoutOwnership: session.checkoutOwnership ?? {},
          pending: false,
          sessionChecked: true,
          sessionError: null,
        });
      } catch (err) {
        set({ pending: false });
        throw err;
      }
    },

    async resendVerificationEmail(email, turnstileToken) {
      set({ pending: true });
      try {
        await authApi.resendVerificationEmail(email, turnstileToken);
        set({ pending: false });
      } catch (err) {
        set({ pending: false });
        throw err;
      }
    },

    async loginAsDemo() {
      set({ pending: true });
      const session = await authApi.loginAsDemo();
      setAuthToken(session.token);
      set({ ...session, pending: false, sessionChecked: true, sessionError: null });
    },

    logout() {
      // Fire-and-forget: the mock cannot fail, and the UI should not wait to log
      // out. A real implementation still clears local state immediately — a failed
      // logout request must never leave the user apparently signed in.
      void authApi.logout();
      clearSessionState();
    },

    clearSession() {
      clearSessionState();
    },

    async updateProfile(patch) {
      const applied = await authApi.updateProfile(patch);
      const current = get().user;
      if (!current) return;
      set({ user: { ...current, ...applied } });
    },
  };
});

/*
  ---------------------------------------------------------------------------
  THE CENTRAL 401 PATH — registered once, here, at module scope.

  WHAT A 401 MEANS AND WHAT IT DOES. The server says the credential it was sent is not
  good any more. api/http.ts has already cleared the token by the time this runs; this is
  the other half — clear the session state, so the router's gate sees an unauthenticated
  user and sends them to /login. One handler, one reaction, which is what "centralized 401
  handling" has to mean: a per-module response would have each caller deciding separately
  whether an expired session is its problem, and they would not agree.

  WHY HERE RATHER THAN IN A COMPONENT OR AN ENTRY FILE. api/http.ts cannot import a store
  (api/README.md: nothing in that directory touches the state layer), so the dependency
  has to be injected from this side. Doing it at module scope rather than in a `useEffect`
  means the handler is attached for every entry point that imports this store — the app,
  and the tests — instead of only once a particular component has mounted. It is a
  callback registration and nothing else: no request, no timer, no state change at import.

  NOT A REFRESH-AND-RETRY. There is no refresh token in this app and
  `authApi.refreshSession()` throws 501; see the transport-mechanism note in api/http.ts.
  Signing out is the honest response until the backend defines refresh.

  `logout()` rather than a bare `set`, so the sign-out path is one implementation: it also
  clears the transport token and keeps `sessionChecked` true, which is what stops the gate
  showing its checking splash on the way to the login screen.
  ---------------------------------------------------------------------------
*/
setUnauthorizedHandler(() => {
  // Guarded: a 401 answering a public request made while nobody was signed in is not a
  // sign-out, and firing one would clear a `sessionError` the gate is displaying.
  if (useAuthStore.getState().isAuthenticated) useAuthStore.getState().logout();
});

/*
  ---------------------------------------------------------------------------
  THESE TWO USED TO INVENT AN IDENTITY, AND NO LONGER DO.

  They were `s.user?.name ?? 'Goodluck Efe'` and `s.user?.email ?? 'goodluck.efe@example.com'`
  — the prototype's fallbacks, carried forward. Batch 7 §5: "The frontend must not
  permanently depend on hardcoded/mock user identity", and a selector that answers a real
  person's name and address for an account that does not exist is the clearest form of
  that dependency. It was not hypothetical: the licence certificate reads
  `selectProfileEmail` and derives the licence number from it, so an unauthenticated read
  produced a certificate in a named person's name with a plausible licence number on it.

  EMPTY STRING, NOT A NAME. Every consumer of these is rendered inside `RequireAuth`, so
  a null user is unreachable in practice and the empty branch is the belt-and-braces one —
  but "unreachable" is not "safe to fill with a name". The three surfaces that would show
  something odd for an empty name each handle it explicitly now: the two greetings omit
  the name, and `Avatar` falls back to a neutral mark rather than the demo account's
  initials.

  STILL STRINGS, NOT `string | null`. Changing the type would touch a dozen call sites,
  including two that interpolate into HTML for the certificate, for no gain — the falsy
  check a caller needs is the same either way, and `LocalLicenseContext.customerName` is
  typed `string`. See lib/attribution for the one place where null IS the right type,
  because there the absence has to suppress an entire sentence.
  ---------------------------------------------------------------------------
*/

/** Display name, or '' when nobody is signed in. Never a stand-in name. */
export const selectProfileName = (s: AuthState): string => s.user?.name ?? '';
/** Account email, or '' when nobody is signed in. Never a stand-in address. */
export const selectProfileEmail = (s: AuthState): string => s.user?.email ?? '';
/**
 * Profile picture, or null when there is none.
 *
 * No fallback image: the absence is meaningful, and every avatar surface answers it
 * by drawing the account's initials — see components/account/Avatar.tsx. Returning
 * null rather than undefined so a selector result is a stable value under Object.is
 * whichever branch it takes.
 */
export const selectAvatarUrl = (s: AuthState): string | null => s.user?.avatarUrl ?? null;
